import { useRealtimeKitClient } from "@cloudflare/realtimekit-react";
import { RtkParticipantsAudio } from "@cloudflare/realtimekit-react-ui";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";

import { useDesktopBridge } from "../desktop-bridge/use-desktop-bridge.js";
import { useFluid } from "../fluid/hooks/use-fluid.js";
import { useOffice } from "../office/use-office.js";
import { useCurrentUserTablePresence } from "../presence/use-my-table-presence.js";
import { TableCallDesktopBridgeListeners } from "./table-call-desktop-bridge-listeners.js";
import {
  getTableCallStatus,
  TableCallParticipantDirectory,
} from "./table-call-participant-directory.js";
import type { LocalCallPhase } from "./table-call-participant-directory.js";
import { TableCallSpeakingTracker } from "./table-call-speaking-tracker.js";
import { TableCallContext } from "./table-call.context.js";
import type {
  MeetingClient,
  TableCallAudioReadiness,
  TableCallContextType,
} from "./table-call.context.js";
import type { TableCallTarget } from "./table-call.types.js";
import { useTableCallDeviceController } from "./use-table-call-device-controller.js";
import { useTableCallDiagnostics } from "./use-table-call-diagnostics.js";
import { useTableCallRuntime } from "./use-table-call-runtime.js";
import { useTableCallSession } from "./use-table-call-session.js";

export const TableCallProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { status: bridgeStatus } = useDesktopBridge();
  const { containerId } = useFluid();
  const { tableCallTokens, tables, team } = useOffice();
  const { tableId: currentTableId } = useCurrentUserTablePresence();
  const tableId = useMemo(
    () =>
      currentTableId !== null &&
      tables.some((table) => table.id === currentTableId)
        ? currentTableId
        : null,
    [currentTableId, tables]
  );
  const target = useMemo<TableCallTarget | null>(
    () =>
      tableId && containerId
        ? { fluidId: containerId, key: `${tableId}:${containerId}`, tableId }
        : null,
    [containerId, tableId]
  );
  const [audioReadiness, setAudioReadiness] =
    useState<TableCallAudioReadiness>("disconnected");
  const [audioIssueMessage, setAudioIssueMessage] = useState<string | null>(
    null
  );
  const [_meeting, initClient] = useRealtimeKitClient({ resetOnLeave: true });
  const [meetingClient, setMeetingClient] = useState<MeetingClient | null>(
    null
  );
  const participantDirectory = useMemo(
    () => new TableCallParticipantDirectory(),
    []
  );
  const subscribeLocalCall = useCallback(
    (listener: () => void) => participantDirectory.subscribeLocalCall(listener),
    [participantDirectory]
  );
  const getLocalCallSnapshot = useCallback(
    () => participantDirectory.getLocalCallSnapshot(),
    [participantDirectory]
  );
  const localCallSnapshot = useSyncExternalStore(
    subscribeLocalCall,
    getLocalCallSnapshot,
    getLocalCallSnapshot
  );
  const status = getTableCallStatus(localCallSnapshot.phase);
  const setRtkMeeting = useCallback(
    (
      meeting: MeetingClient | null,
      generation: number,
      lifecycleHandlers?: {
        getRoomLeftPhase?: (state: string) => LocalCallPhase;
        retainRemotesOnRoomLeft?: (state: string) => boolean;
        isCurrent?: () => boolean;
        onRoomJoined?: () => void;
        onRoomLeft?: (state: string) => void;
      }
    ) => {
      participantDirectory.setMeeting(meeting, generation, lifecycleHandlers);
    },
    [participantDirectory]
  );
  const setLocalCallPhase = useCallback(
    (phase: LocalCallPhase, generation: number) => {
      participantDirectory.setLocalCallPhase(phase, generation);
    },
    [participantDirectory]
  );
  const runtime = useTableCallRuntime(status, audioReadiness);
  const tokenCacheRef = useRef(new Map<string, string>());
  useEffect(() => {
    if (containerId !== team.fluidContainerId) {
      tokenCacheRef.current = new Map();
      return;
    }

    tokenCacheRef.current = new Map(
      tableCallTokens.map(({ rtkMeetingAuthToken, tableId: tokenTableId }) => [
        `${tokenTableId}:${containerId}`,
        rtkMeetingAuthToken,
      ])
    );
  }, [containerId, team.fluidContainerId, tableCallTokens]);
  const setAudioState = useCallback(
    (readiness: TableCallAudioReadiness, message: string | null = null) => {
      if (runtime.isMountedRef.current) {
        setAudioReadiness(readiness);
        setAudioIssueMessage(message);
      }
    },
    [runtime]
  );
  const diagnostics = useTableCallDiagnostics(runtime, participantDirectory);
  const isCurrentOperation = useCallback(
    (args: {
      generation: number;
      target: TableCallTarget;
      targetMeeting: MeetingClient;
    }) =>
      runtime.isMountedRef.current &&
      runtime.desiredTargetRef.current?.key === args.target.key &&
      runtime.meetingRef.current === args.targetMeeting &&
      runtime.operationGenerationRef.current === args.generation,
    [runtime]
  );
  const {
    canControlMicrophone,
    canEnableMicrophone,
    clearMeetingState,
    initializeMeetingState,
    microphoneActive,
    microphoneAlwaysOn,
    prepareJoinedMeeting: prepareJoinedDeviceMeeting,
    resetDeviceState,
    resetMicrophoneState,
    setMicrophoneActive,
    setMicrophoneAlwaysOn,
    setPreferredMicrophone,
    setPreferredSpeaker,
    startPushToTalk,
    stopDeviceApplication,
    stopPushToTalk,
  } = useTableCallDeviceController({
    diagnostics,
    isCurrentOperation,
    meeting: meetingClient,
    runtime,
    setAudioState,
    status,
  });
  const prepareJoinedMeeting = async (
    client: MeetingClient,
    joinTarget: TableCallTarget,
    generation: number
  ) => {
    const publishReady = await prepareJoinedDeviceMeeting(
      client,
      joinTarget,
      generation
    );
    return isCurrentOperation({
      generation,
      target: joinTarget,
      targetMeeting: client,
    })
      ? publishReady
      : null;
  };
  const session = useTableCallSession({
    clearMeetingState,
    initClient,
    initializeMeetingState,
    onMeetingChanged: setMeetingClient,
    prepareJoinedMeeting,
    resetDeviceState,
    resetMicrophoneState,
    runtime,
    setAudioState,
    setLocalCallPhase,
    setRtkMeeting,
    stopDeviceApplication,
    target,
    tokenCacheRef,
  });
  const appMeeting = status === "connected" ? session.currentMeeting : null;
  const participantsAudioMeeting =
    localCallSnapshot.phase === "joined" ||
    localCallSnapshot.phase === "reconnecting"
      ? session.currentMeeting
      : null;
  useEffect(
    () => () => {
      participantDirectory.dispose();
    },
    [participantDirectory]
  );
  const contextValue = useMemo<TableCallContextType>(
    () => ({
      __ctx_init: true,
      audioIssueMessage,
      audioReadiness,
      canControlMicrophone,
      canEnableMicrophone,
      meeting: appMeeting,
      microphoneActive,
      microphoneAlwaysOn,
      participantDirectory,
      sessionPhase: localCallSnapshot.phase,
      setMicrophoneActive,
      setMicrophoneAlwaysOn,
      setPreferredMicrophone,
      setPreferredSpeaker,
      startPushToTalk,
      status,
      stopPushToTalk,
    }),
    [
      appMeeting,
      audioIssueMessage,
      audioReadiness,
      canControlMicrophone,
      canEnableMicrophone,
      participantDirectory,
      localCallSnapshot.phase,
      microphoneActive,
      microphoneAlwaysOn,
      status,
      setMicrophoneActive,
      setMicrophoneAlwaysOn,
      setPreferredMicrophone,
      setPreferredSpeaker,
      startPushToTalk,
      stopPushToTalk,
    ]
  );
  return (
    <TableCallContext.Provider value={contextValue}>
      <TableCallSpeakingTracker
        meeting={appMeeting}
        microphoneActive={microphoneActive}
        participantDirectory={participantDirectory}
        tableId={tableId}
      />
      <TableCallDesktopBridgeListeners enabled={bridgeStatus === "ready"} />
      {children}
      {participantsAudioMeeting && (
        <RtkParticipantsAudio meeting={participantsAudioMeeting} />
      )}
    </TableCallContext.Provider>
  );
};
